import React, { FC, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import debounce from 'lodash/debounce';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';

import { buttonVariants } from '@clatter/ui';
import { useDeepCompareEffect, useDeepCompareMemo } from '@clatter/platform';
import { StyledError } from '../UI';
import { DynamicFormField, NgButton } from '../../../UI';
import { FolderManagerActionTypes } from '../../../../interfaces';

interface FolderManagerDynamicFormProps {
  formSchema: any;
  initialValues?: any;
  isLoading: boolean;
  customFormMethods?: any; // we can pass it to the component so parent would also have access to it
  buttonsTargetContainerId?: string | null | undefined;
  onAction: (action: any) => void;
  onChange?: (form: any) => void;
  errors?: any;
  minWidth?: string;
  noMargin?: boolean;
  clearAfterSubmit?: boolean;
  submitButtonLabel?: string;
  resetButtonLabel?: string;
  hideResetButton?: boolean;
  noChildrenGap?: boolean;
  hideButtons?: boolean;
  includeCancelButton?: boolean;
  customComponents?: any;
  children?: React.ReactNode;
}

const StyledButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .right-side {
    margin-left: auto;
    display: flex;
    justify-content: flex-end;
    gap: 15px;
  }
`;

const StyledForm = styled.form<{ $noMargin: boolean; $minWidth: string; $noChildrenGap: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  gap: ${({ $noChildrenGap, theme }) => ($noChildrenGap ? '0' : theme.spacing.medium)};
  min-width: ${({ $minWidth }) => $minWidth};
  flex-grow: 1;
  margin: ${({ $noMargin }) => ($noMargin ? 'none' : '10px auto 30px')};
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  input,
  select {
    padding: 7px 5px;
  }

  &.error {
    input {
      border-color: red;
    }
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 10px;
  font-size: 16px;
  border: 2px solid grey;
  border-radius: 5px;
  resize: vertical;
`;

interface FormButtonsProps {
  isLoading: boolean;
  isDirty: boolean;
  isValid: boolean;
  reset: () => void;
  handleSubmit: any;
  submitButtonLabel?: string;
  resetButtonLabel?: string;
  hideResetButton?: boolean;
  includeCancelButton?: boolean;
  onAction: (actionType: FolderManagerActionTypes) => void;
}

const sortFields = (fields: any[], predefinedOrder: string[] = []) => {
  return [...fields].sort((a, b) => {
    const orderA = a.inputOptions?.order ?? a.order;
    const orderB = b.inputOptions?.order ?? b.order;

    // if both have explicit order → compare directly
    if (orderA != null && orderB != null) {
      return orderA - orderB;
    }

    // if only one has order → that one goes first
    if (orderA != null) return -1;
    if (orderB != null) return 1;

    // neither has explicit order → fallback to predefined list
    const indexA = predefinedOrder.indexOf(a.name);
    const indexB = predefinedOrder.indexOf(b.name);

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // neither in predefined list → preserve original order
    return fields.indexOf(a) - fields.indexOf(b);
  });
};

const FormButtons: FC<FormButtonsProps> = ({
  isLoading,
  isDirty,
  isValid,
  onAction,
  reset,
  includeCancelButton,
  submitButtonLabel = 'Submit',
  resetButtonLabel = 'Reset',
  handleSubmit,
  hideResetButton,
}) => (
  <StyledButtonsContainer>
    <div className="left-side">
      {!hideResetButton && (
        <NgButton
          disabled={isLoading || !isDirty}
          //@ts-ignore
          icon={ClearIcon}
          variant={buttonVariants.clear}
          onClick={() => reset()}
          type="button"
          testId="dynamic-form-reset-button"
        >
          {resetButtonLabel}
        </NgButton>
      )}
    </div>
    <div className="right-side">
      {includeCancelButton && (
        <NgButton
          disabled={isLoading}
          //@ts-ignore
          icon={CloseIcon}
          variant={buttonVariants.clear}
          onClick={() => onAction(FolderManagerActionTypes.CANCEL)}
          type="button"
          testId="dynamic-form-reset-button"
        >
          Cancel
        </NgButton>
      )}

      <NgButton
        disabled={isLoading || !isDirty || !isValid}
        icon={DoneIcon}
        onClick={() => handleSubmit()}
        variant={buttonVariants.primary}
        testId="dynamic-form-submit-button"
      >
        {submitButtonLabel}
      </NgButton>
    </div>
  </StyledButtonsContainer>
);

const fieldsOrder = ['first_name', 'last_name', 'email', 'phone'];

export const FolderManagerDynamicForm: FC<FolderManagerDynamicFormProps> = ({
  formSchema,
  buttonsTargetContainerId,
  initialValues,
  onAction,
  onChange,
  isLoading,
  children,
  minWidth = '40vw',
  errors: apiErrors,
  submitButtonLabel,
  resetButtonLabel,
  noMargin = false,
  customFormMethods,
  includeCancelButton = false,
  hideResetButton = false,
  hideButtons = false,
  clearAfterSubmit = false,
  noChildrenGap = false,
  customComponents,
}) => {
  const { fields: unsortedFields, validation } = formSchema || {};
  const targetContainer = buttonsTargetContainerId ? document.getElementById(buttonsTargetContainerId) : null;

  //@todo - this is first naive implementation
  const fields = useDeepCompareMemo(() => sortFields(unsortedFields, fieldsOrder), [unsortedFields, fieldsOrder]);

  const internalFormMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(validation, {
      formats: fullFormats,
    }),
  });

  const formMethods = customFormMethods || internalFormMethods;

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    setFocus,
    formState: { errors, isDirty, isValid: isFormValid },
  } = formMethods;

  // const isValid = !Object.keys(errors)?.length && isFormValid;
  const isValid = isFormValid;
  const formState = watch();

  const onSubmit = async () => {
    const formData = getValues();
    onAction(formData);
    reset(clearAfterSubmit ? initialValues : formData);
  };

  const debouncedOnChange = useMemo(() => {
    if (typeof onChange !== 'function') return undefined;

    return debounce(onChange, 300); // 300ms debounce
  }, [onChange]);

  useDeepCompareEffect(() => {
    reset(initialValues);
  }, [initialValues]);

  useDeepCompareEffect(() => {
    if (debouncedOnChange) {
      debouncedOnChange(formState);
    }
  }, [formState, debouncedOnChange]);

  // clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange?.cancel();
    };
  }, [debouncedOnChange]);

  useEffect(() => {
    const firstField = fields?.[0]?.name;
    if (!firstField) return;
    const id = setTimeout(() => setFocus(firstField), 50);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formButtons = (
    <FormButtons
      isLoading={isLoading}
      isDirty={isDirty}
      isValid={isValid}
      reset={reset}
      handleSubmit={onSubmit}
      onAction={onAction}
      resetButtonLabel={resetButtonLabel}
      submitButtonLabel={submitButtonLabel}
      includeCancelButton={includeCancelButton}
      hideResetButton={hideResetButton}
    />
  );

  return (
    <FormProvider {...formMethods}>
      <StyledForm
        $minWidth={minWidth}
        $noChildrenGap={noChildrenGap}
        $noMargin={noMargin}
        onSubmit={handleSubmit(onSubmit)}
      >
        <StyledFormInputsContainer>
          {(fields || []).map((field: any, index: number) => {
            const customComponent = customComponents?.[field.name];

            return (
              <div key={field.name || index} style={field?.inputType === 'hidden' ? { display: 'none' } : {}}>
                {!field?.inputOptions?.hideLabel && <label htmlFor={field?.name}>{field.label}</label>}
                <StyledInputContainer>
                  {customComponent ? (
                    // if custom field is provided, render it
                    customComponent
                  ) : (
                    // fallback to default DynamicFormField
                    <DynamicFormField data={field} disabled={isLoading} register={register} />
                  )}
                </StyledInputContainer>
                <StyledError>{errors?.[field?.name]?.message || '\u00A0'}</StyledError>
              </div>
            );
          })}

          {apiErrors && <StyledError>{apiErrors}</StyledError>}
        </StyledFormInputsContainer>

        {children}

        {!hideButtons &&
          (buttonsTargetContainerId && targetContainer
            ? ReactDOM.createPortal(formButtons, document.getElementById(buttonsTargetContainerId) as HTMLElement)
            : formButtons)}
      </StyledForm>
    </FormProvider>
  );
};
